import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Snackbar,
  CircularProgress,
  Grid,
  Alert,
} from '@mui/material';
import { settingsAPI, handleAPIError } from '../services/api';

// The only settings keys the backend whitelist accepts.
const DEFAULT_SETTINGS = {
  theme: 'dark', // legacy (unused by the chat UI); kept so it round-trips
  defaultTheme: 'midnight', // default chat visual theme; users can override
  primaryColor: '#00d9ff',
  backgroundColor: '#0f1115',
  imageUploadsEnabled: false,
  statsEnabled: false,
  watchAckEnabled: true, // default ON (backend treats missing as enabled)
  voiceLimiterEnabled: true, // default ON (backend treats missing as enabled)
  friendsEnabled: true, // default ON (backend treats missing as enabled)
  // Sign-in methods — default ON (backend treats missing as enabled).
  guestLoginEnabled: true,
  registrationEnabled: true,
  // Guest permissions — default OFF (backend treats missing as denied).
  guestsCanUploadImages: false,
  guestsCanSendFiles: false,
  guestsCanPrivateChat: false,
  // AFK idle policy — OFF by default (it disconnects people).
  idleKickEnabled: false,
  idleKickMinutes: 25,
  idleWarnMinutes: 5,
};

// Clamp a number field to the backend's validator range. The settings whitelist
// is FAIL-CLOSED — one bad value rejects the entire patch — and a
// <TextField type="number"> hands us a STRING ("25"), which the backend's
// Number.isFinite check rejects. Coercing here makes that 400 structurally
// impossible while still letting the user clear the field mid-edit.
const clampInt = (v, lo, hi, dflt) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : dflt;
};

// Stored settings JSON can carry legacy/operator-managed keys (e.g. old seeds
// wrote allowGuests/allowRegistration). The save endpoint fail-closes on any
// key outside its whitelist, so keep only known keys in form state — the
// backend merges patches onto stored settings, so unknown keys survive there.
const pickKnownSettings = (raw) => {
  const clean = { ...DEFAULT_SETTINGS };
  for (const key of Object.keys(DEFAULT_SETTINGS)) {
    if (raw && raw[key] !== undefined) clean[key] = raw[key];
  }
  return clean;
};

const SettingsPage = () => {
  const { tenantId, scriptHash } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState(null); // {severity, message}

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await settingsAPI.get(tenantId, scriptHash);
      setName(data.name || '');
      setDescription(data.description || '');
      setSettings(pickKnownSettings(data.settings));
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  }, [tenantId, scriptHash]);

  useEffect(() => {
    load();
  }, [load]);

  const setSetting = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await settingsAPI.update(tenantId, scriptHash, {
        name,
        description,
        settings: {
          ...settings,
          idleKickMinutes: clampInt(settings.idleKickMinutes, 5, 480, 25),
          idleWarnMinutes: clampInt(settings.idleWarnMinutes, 1, 30, 5),
        },
      });
      setName(updated.name || '');
      setDescription(updated.description || '');
      setSettings(pickKnownSettings(updated.settings));
      setSnack({ severity: 'success', message: 'Settings saved' });
    } catch (err) {
      setSnack({ severity: 'error', message: handleAPIError(err) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h4" gutterBottom>
        Chat settings
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Manage your chat’s name, description, and appearance.
      </Typography>

      {error && (
        <Alert severity="error" style={{ margin: '16px 0' }}>
          {error}
        </Alert>
      )}

      <Card style={{ marginTop: 16 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            General
          </Typography>
          <TextField
            label="Chat name"
            variant="outlined"
            fullWidth
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 80 } }}
          />
          <TextField
            label="Description"
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </CardContent>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Appearance & features
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl variant="outlined" fullWidth margin="normal">
                <InputLabel>Default chat theme</InputLabel>
                <Select
                  label="Default chat theme"
                  value={settings.defaultTheme}
                  onChange={(e) => setSetting('defaultTheme', e.target.value)}
                >
                  <MenuItem value="midnight">Midnight (teal / slate)</MenuItem>
                  <MenuItem value="sesh">Sesh (green / ember)</MenuItem>
                </Select>
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                  The starting theme for everyone in this chat. Each user can still
                  pick their own from the chat top bar.
                </Typography>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                label="Primary color"
                type="color"
                variant="outlined"
                fullWidth
                margin="normal"
                value={settings.primaryColor}
                onChange={(e) => setSetting('primaryColor', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                label="Background"
                type="color"
                variant="outlined"
                fullWidth
                margin="normal"
                value={settings.backgroundColor}
                onChange={(e) => setSetting('backgroundColor', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!settings.imageUploadsEnabled}
                    onChange={(e) => setSetting('imageUploadsEnabled', e.target.checked)}
                  />
                }
                label="Allow image uploads"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!settings.statsEnabled}
                    onChange={(e) => setSetting('statsEnabled', e.target.checked)}
                  />
                }
                label="Show member usage stats (time online, button clicks, 420s/350s, hits taken)"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={settings.watchAckEnabled !== false}
                    onChange={(e) => setSetting('watchAckEnabled', e.target.checked)}
                  />
                }
                label={'Show "You are now watching…" chat notice when a public webcam watch starts'}
              />
              <Typography variant="caption" color="textSecondary" component="div">
                Only affects public (auto-accepted) cams. When a private cam owner
                approves a request, the viewer always gets the confirmation.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={settings.voiceLimiterEnabled !== false}
                    onChange={(e) => setSetting('voiceLimiterEnabled', e.target.checked)}
                  />
                }
                label="Voice limiter — caps microphone peaks so shouting can't clip or blast listeners"
              />
              <Typography variant="caption" color="textSecondary" component="div">
                Recommended on. Only touches audio above -1 dB; normal speech is
                unaffected. Turn off only if your community wants fully
                unprocessed voice.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={settings.friendsEnabled !== false}
                    onChange={(e) => setSetting('friendsEnabled', e.target.checked)}
                  />
                }
                label="Friends — let users send and accept friend requests"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Who can get in at all. Its own card, above guest permissions: these
          two decide whether new people can join, the card below decides what
          guests can do once they're in. Both ON by default. */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sign-in
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Choose how new people can join. Existing members can always sign
            in, and anyone already in the chat stays connected when you turn
            one of these off.
          </Typography>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={settings.guestLoginEnabled !== false}
                    onChange={(e) => setSetting('guestLoginEnabled', e.target.checked)}
                  />
                }
                label="Allow guest login"
              />
              <Typography variant="caption" color="textSecondary" component="div">
                Shows "Continue as Guest" on the sign-in screen. Guests get a
                random name and need no account.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={settings.registrationEnabled !== false}
                    onChange={(e) => setSetting('registrationEnabled', e.target.checked)}
                  />
                }
                label="Allow new sign-ups"
              />
              <Typography variant="caption" color="textSecondary" component="div">
                Shows the Sign Up tab, so anyone can create a member account.
              </Typography>
            </Grid>
            {settings.guestLoginEnabled === false && settings.registrationEnabled === false && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  With both off, your chat is members-only: nobody new can get
                  in until you turn one back on.
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Guest permissions live in their own card, not among the appearance
          toggles: these decide what an anonymous user can do, and burying a
          permission switch in a list of cosmetic ones is how it gets flipped by
          accident. All three are OFF by default. */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Guest permissions
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Guests sign in without an email and can return under a new name at
            any time, so these are off by default. Turn one on only if your
            community expects it — each is enforced on the server, so a guest
            simply cannot do it while the switch is off.
          </Typography>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!settings.guestsCanUploadImages}
                    onChange={(e) => setSetting('guestsCanUploadImages', e.target.checked)}
                  />
                }
                label="Guests can upload images"
              />
              <Typography variant="caption" color="textSecondary" component="div">
                Covers the attach button, drag-and-drop, paste and the drawing
                tool. Uploaded images are stored on the chat server.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!settings.guestsCanSendFiles}
                    onChange={(e) => setSetting('guestsCanSendFiles', e.target.checked)}
                  />
                }
                label="Guests can send and receive files"
              />
              <Typography variant="caption" color="textSecondary" component="div">
                Peer-to-peer file transfer. Blocked in both directions while off,
                so members cannot send files to a guest either.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!settings.guestsCanPrivateChat}
                    onChange={(e) => setSetting('guestsCanPrivateChat', e.target.checked)}
                  />
                }
                label="Guests can use private chat"
              />
              <Typography variant="caption" color="textSecondary" component="div">
                One-to-one private messages. Blocked in both directions while
                off, so a member cannot open a private chat with a guest either.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Inactivity is policy, not appearance — its own card for the same
          reason as guest permissions: this one disconnects people. */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Inactivity
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            People who join, turn on a webcam and walk away keep using bandwidth
            indefinitely. With this on, anyone showing no activity gets a warning
            and is then disconnected. They stay signed in and can rejoin with one
            click.
          </Typography>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!settings.idleKickEnabled}
                    onChange={(e) => setSetting('idleKickEnabled', e.target.checked)}
                  />
                }
                label="Disconnect inactive users"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Minutes before warning"
                margin="normal"
                disabled={!settings.idleKickEnabled}
                value={settings.idleKickMinutes}
                onChange={(e) => setSetting('idleKickMinutes', e.target.value)}
                slotProps={{ htmlInput: { min: 5, max: 480, step: 5 } }}
                helperText="5–480. Mouse, keyboard and chat activity all count."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Warning length (minutes)"
                margin="normal"
                disabled={!settings.idleKickEnabled}
                value={settings.idleWarnMinutes}
                onChange={(e) => setSetting('idleWarnMinutes', e.target.value)}
                slotProps={{ htmlInput: { min: 1, max: 30, step: 1 } }}
                helperText="1–30. How long the countdown runs before disconnect."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box mt={2} display="flex" gap={1}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={22} /> : 'Save changes'}
        </Button>
        <Button variant="outlined" onClick={load} disabled={saving}>
          Reset
        </Button>
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.severity} onClose={() => setSnack(null)}>
            {snack.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
